import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ApiError, errorMessage } from '../api/errors';
import { Icon } from './icons';

// ------------------------------------------------------------------ botones
type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost';

export function Button({
  variant = 'default',
  size,
  busy,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm';
  busy?: boolean;
}) {
  const classes = [
    'btn',
    variant !== 'default' && `btn-${variant}`,
    size && `btn-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={classes}
      disabled={busy || props.disabled}
      aria-busy={busy || undefined}
      {...props}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ campos
interface FieldProps {
  label: string;
  error?: string;
  help?: string;
  /** Explicación corta que aparece al pasar el mouse por el ícono ⓘ junto a la etiqueta. */
  hint?: string;
}

/**
 * Ícono ⓘ con una explicación corta: aparece al pasar el mouse, al enfocarlo con el teclado o al
 * tocarlo en el celular. Se dibuja sobre la página (portal) para que una tabla con desplazamiento
 * no la recorte. No es un botón, para que dentro de una etiqueta no marque la casilla.
 */
export function InfoTip({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);
  // Las ventanas (<dialog>) viven en la capa superior del navegador, encima de cualquier z-index:
  // la explicación también va ahí (popover) para no quedar detrás de la ventana.
  useEffect(() => {
    const el = tip.current;
    if (pos && el && typeof el.showPopover === 'function' && !el.matches(':popover-open'))
      el.showPopover();
  }, [pos]);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const half = 135;
    const left = Math.min(Math.max(r.left + r.width / 2, half + 8), window.innerWidth - half - 8);
    const below = r.top < 90;
    setPos({ left, top: below ? r.bottom + 8 : r.top - 8, below });
  };
  const hide = () => setPos(null);
  // Al desplazar la página la explicación quedaría fuera de lugar: se cierra.
  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [pos]);
  return (
    <span
      ref={ref}
      className="infotip"
      tabIndex={0}
      role="note"
      aria-label={text}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.focus();
        show();
      }}
    >
      <span aria-hidden>i</span>
      {pos &&
        createPortal(
          <span
            ref={tip}
            popover="manual"
            className={pos.below ? 'infotip-text below' : 'infotip-text'}
            style={{ left: pos.left, top: pos.top }}
            aria-hidden
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

/**
 * Texto con su explicación ⓘ al lado (si la hay). La última palabra va pegada al ícono para que
 * este nunca quede solo en la línea siguiente.
 */
export function WithHint({ children, hint }: { children: ReactNode; hint?: string | null }) {
  if (!hint) return <>{children}</>;
  if (typeof children === 'string') {
    const cut = children.lastIndexOf(' ');
    return (
      <>
        {cut >= 0 ? children.slice(0, cut + 1) : ''}
        <span className="nowrap">
          {children.slice(cut + 1)}
          <InfoTip text={hint} />
        </span>
      </>
    );
  }
  return (
    <>
      {children}
      <InfoTip text={hint} />
    </>
  );
}

function FieldShell({
  id,
  label,
  error,
  help,
  hint,
  children,
}: FieldProps & { id: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>
        <WithHint hint={hint}>{label}</WithHint>
      </label>
      {children}
      {error ? (
        <span className="error" id={`${id}-error`}>
          {error}
        </span>
      ) : help ? (
        <span className="help">{help}</span>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  error,
  help,
  hint,
  ...props
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} error={error} help={help} hint={hint}>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...props}
      />
    </FieldShell>
  );
}

export function SelectField({
  label,
  error,
  help,
  hint,
  options,
  placeholder,
  ...props
}: FieldProps &
  SelectHTMLAttributes<HTMLSelectElement> & {
    options: { value: string; label: string }[];
    placeholder?: string;
  }) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} error={error} help={help} hint={hint}>
      <select id={id} aria-invalid={error ? true : undefined} {...props}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export function TextArea({
  label,
  error,
  help,
  hint,
  ...props
}: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} error={error} help={help} hint={hint}>
      <textarea id={id} aria-invalid={error ? true : undefined} {...props} />
    </FieldShell>
  );
}

export function Check({
  label,
  hint,
  ...props
}: { label: string; hint?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="check">
      <input type="checkbox" {...props} />
      <WithHint hint={hint}>{label}</WithHint>
    </label>
  );
}

// ------------------------------------------------------------------ estructura
export function PageHeader({
  title,
  description,
  hint,
  actions,
}: {
  title: string;
  description?: string;
  /** Explicación en el ícono ⓘ junto al título, para pantallas donde el espacio vale más. */
  hint?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>
          <WithHint hint={hint}>{title}</WithHint>
        </h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={['card', className].filter(Boolean).join(' ')}>
      {(title || actions) && (
        <div className="card-title">
          {title && <h2>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Wordmark: símbolo + EATHERIX (IX en el acento, como en el logo madre) y el producto de la
 * empresa. Sin producto (ingreso y registro, antes de saber la empresa) va solo EATHERIX.
 */
export function Wordmark({ product }: { product?: string }) {
  return (
    <span className="wordmark">
      <img src="/logo-mark.png" alt="" className="wordmark-mark" />
      <span>
        EATHER<span className="wordmark-accent">IX</span>
      </span>
      {product && <span className="wordmark-product">{product}</span>}
    </span>
  );
}

type Tone = 'neutral' | 'brand' | 'ok' | 'warn' | 'danger';
export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={['badge', tone !== 'neutral' && `badge-${tone}`, className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}

export const ORDER_TONE: Record<string, Tone> = {
  draft: 'neutral',
  confirmed: 'brand',
  fulfilled: 'ok',
  delivered: 'ok',
  cancelled: 'danger',
};
export const PAYMENT_TONE: Record<string, Tone> = {
  unpaid: 'warn',
  partial: 'brand',
  paid: 'ok',
};

// ------------------------------------------------------------------ estados
export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack" style={{ padding: 16 }} role="status" aria-label="Cargando">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <strong>{title}</strong>
      {children}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

/** Aviso de que el formulario trae lo que la persona había escrito antes (borrador recuperado). */
export function DraftNotice({ draft }: { draft: { restored: boolean; discard: () => void } }) {
  if (!draft.restored) return null;
  return (
    <div className="alert alert-info draft-notice" role="status">
      <span>Recuperamos lo que llevabas escribiendo.</span>
      <Button size="sm" variant="ghost" onClick={draft.discard}>
        Descartar y empezar de nuevo
      </Button>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  // Sin error no se muestra nada: varias pantallas dibujan la caja siempre y salía «Ocurrió un
  // error inesperado» al abrir un formulario.
  if (!error) return null;
  // La referencia solo sirve para que soporte rastree un error inesperado del servidor; en los
  // avisos de negocio (límite del plan, dato inválido) es ruido para la persona.
  const requestId = error instanceof ApiError && error.status >= 500 ? error.requestId : undefined;
  return (
    <div className="alert" role="alert">
      {errorMessage(error, { withFields: true })}
      {requestId && <span className="mono muted"> · ref {requestId.slice(0, 8)}</span>}
      {onRetry && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ tabla
export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  numeric?: boolean;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}) {
  if (rows.length === 0)
    return <div className="table-wrap">{empty ?? <Empty title="Sin resultados" />}</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.header} className={c.numeric ? 'num' : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? 'clickable' : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {columns.map((c) => (
                <td key={c.header} className={c.numeric ? 'num' : undefined}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------ diálogo
/** Bloquea el desplazamiento de la página (con contador: puede haber diálogos anidados). */
let scrollLocks = 0;
function lockPageScroll(): () => void {
  scrollLocks += 1;
  if (scrollLocks === 1) document.documentElement.classList.add('modal-open');
  return () => {
    scrollLocks -= 1;
    if (scrollLocks === 0) document.documentElement.classList.remove('modal-open');
  };
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);
  // Mientras haya un diálogo abierto, la página de atrás no se desplaza.
  useEffect(() => {
    if (!open) return;
    return lockPageScroll();
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className="modal"
      onClose={onClose}
      onCancel={onClose}
      open={typeof HTMLDialogElement === 'undefined' ? true : undefined}
    >
      <header>
        <h2>{title}</h2>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
          ✕
        </Button>
      </header>
      <div className="body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </dialog>
  );
}

// ------------------------------------------------------------------ pestañas
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: string; disabled?: boolean; hint?: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === value}
          disabled={tab.disabled}
          title={tab.hint}
          onClick={() => onChange(tab.id)}
        >
          {tab.disabled && <Icon name="lock" size={13} />}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ notificaciones
interface Toast {
  id: number;
  message: string;
  error?: boolean;
}
const ToastContext = createContext<{
  notify: (message: string) => void;
  fail: (error: unknown) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, error?: boolean) => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list, { id, message, error }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 4500);
  }, []);
  const notify = useCallback((message: string) => push(message), [push]);
  const fail = useCallback(
    (error: unknown) => push(errorMessage(error, { withFields: true }), true),
    [push],
  );
  // Los modales (<dialog> con showModal) viven en la capa superior del navegador, encima de
  // cualquier z-index. Los avisos también van ahí (popover) y se vuelven a mostrar con cada
  // cambio para quedar encima del modal que esté abierto.
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = container.current;
    if (!el || typeof el.showPopover !== 'function') return;
    if (el.matches(':popover-open')) el.hidePopover();
    if (toasts.length > 0) el.showPopover();
  }, [toasts]);
  return (
    <ToastContext.Provider value={{ notify, fail }}>
      {children}
      <div ref={container} popover="manual" className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={t.error ? 'toast toast-error' : 'toast'}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast fuera de ToastProvider');
  return context;
}

export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copiado' : label}
    </Button>
  );
}

export function LoadMore({
  hasMore,
  loading,
  onClick,
}: {
  hasMore: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="load-more">
      <Button onClick={onClick} busy={loading}>
        Cargar más
      </Button>
    </div>
  );
}

/** Errores de campo de un ApiError (400 con errors[]). */
export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fieldErrors : {};
}
